<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import ContactAPI from 'dashboard/api/contacts';

const store = useStore();
const { currentAccount } = useAccount();
const contacts = useMapGetter('contacts/getContactsList');
const meta = useMapGetter('contacts/getMeta');
const uiFlags = useMapGetter('contacts/getUIFlags');

const isFetching = computed(() => uiFlags.value?.isFetching);
const page = ref(1);
const perPage = 50;

const settings = computed(() => currentAccount.value?.settings || {});
const onlyWithPhone = computed(() => {
  const v = settings.value.dustess_only_with_phone;
  return v === undefined ? true : v === true || v === 'true';
});
const pushEnabled = computed(() => {
  const v = settings.value.dustess_enabled;
  return v === true || v === 'true';
});

// 每条名片的推送状态: { [id]: { status: 'pushing'|'ok'|'fail', msg } }
const pushState = ref({});
// 本次会话已成功推送的 id(本地过滤, 不等服务端重拉)
const pushedIds = ref(new Set());
// 本次拉取的 id 快照: 只显示这些, 挡 websocket 实时涌入的访客名片(无电话/静默建会话)
const fetchedIds = ref(new Set());

const totalItems = computed(() => meta.value?.count ?? 0);
const hasMore = computed(() => meta.value?.hasMore ?? false);

const stripPhone = p => (p || '').replace(/^\+86[-\s]?/, '') || '-';

// 保存时间显示: contact_saved_at 是 UTC 字符串(YYYY-MM-DD HH:MM), 按 UTC 解析后显式转北京时间显示
const formatSavedTime = t => {
  if (!t) return '';
  const d = new Date(t.replace(' ', 'T') + ':00Z');
  if (Number.isNaN(d.getTime())) return t;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Shanghai',
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(d);
  const g = type => parts.find(p => p.type === type)?.value || '';
  return `${g('year')}-${g('month')}-${g('day')} ${g('hour')}:${g('minute')}`;
};

// 仅本次拉取的(快照) + 未推送 + (onlyWithPhone 时排除无手机号) + 排除本次已推
const displayList = computed(() =>
  (contacts.value || []).filter(c => {
    if (!c) return false;
    if (!fetchedIds.value.has(c.id)) return false; // 仅本次拉取的, 排除 websocket 涌入
    if (c.customAttributes?.pushedToDustess) return false;
    if (pushedIds.value.has(c.id)) return false;
    if (onlyWithPhone.value && !c.phoneNumber) return false;
    return true;
  })
);

const fetchPage = async p => {
  page.value = p;
  await store.dispatch('contacts/get', {
    page: p,
    perPage,
    sortAttr: '-custom_attributes.contact_saved_at',
  });
  // SET_CONTACTS 已用本次结果替换 sortOrder, 快照其 id
  fetchedIds.value = new Set((contacts.value || []).map(c => c.id));
};

onMounted(() => fetchPage(1));

const pushOne = async contact => {
  pushState.value = {
    ...pushState.value,
    [contact.id]: { status: 'pushing', msg: '' },
  };
  try {
    const res = await ContactAPI.pushToDustess(contact.id);
    const ok = res.data?.ok;
    pushState.value = {
      ...pushState.value,
      [contact.id]: {
        status: ok ? 'ok' : 'fail',
        msg: res.data?.message || (ok ? '推送成功' : '推送失败'),
      },
    };
    if (ok) pushedIds.value = new Set([...pushedIds.value, contact.id]);
  } catch (e) {
    pushState.value = {
      ...pushState.value,
      [contact.id]: {
        status: 'fail',
        msg: e?.response?.data?.error || e?.message || '推送失败',
      },
    };
  }
};

const batchPushing = ref(false);
const batchPush = async () => {
  if (!displayList.value.length) return;
  batchPushing.value = true;
  // 串行逐条推送(尘锋 600/min, 单次≤500, 必须串行)
  for (const c of displayList.value) {
    // eslint-disable-next-line no-await-in-loop
    await pushOne(c);
    // eslint-disable-next-line no-await-in-loop
    await new Promise(r => setTimeout(r, 300));
  }
  batchPushing.value = false;
};

const prev = () => page.value > 1 && fetchPage(page.value - 1);
const next = () => hasMore.value && fetchPage(page.value + 1);
</script>

<template>
  <div class="flex flex-col gap-4 p-6 w-full h-full overflow-y-auto">
    <div class="flex items-center justify-between">
      <div class="flex flex-col">
        <h2 class="text-lg font-semibold text-n-slate-12">未推送名片</h2>
        <span class="text-xs text-n-slate-10"
          >共 {{ totalItems }} 条 · 第 {{ page }} 页（已过滤已推送{{ onlyWithPhone ? ' · 仅显示有手机号' : '' }}）</span
        >
      </div>
      <button
        type="button"
        class="h-9 px-4 rounded-md text-sm font-medium text-white bg-n-brand hover:opacity-90 disabled:opacity-60"
        :disabled="!displayList.length || batchPushing"
        @click="batchPush"
      >
        {{ batchPushing ? '推送中...' : '批量推送' }}
      </button>
    </div>

    <!-- 配置缺失提示 -->
    <div
      v-if="!pushEnabled"
      class="text-xs text-n-amber-12 rounded-md border border-n-weak bg-n-surface-1 px-3 py-2"
    >
      尘锋推送未启用, 请先在「名片推送配置」开启并保存。
    </div>

    <!-- 加载中 -->
    <div
      v-if="isFetching && !contacts.length"
      class="flex items-center justify-center py-20 text-sm text-n-slate-10"
    >
      加载中...
    </div>

    <!-- 空状态 -->
    <div
      v-else-if="!displayList.length"
      class="flex flex-col items-center justify-center py-20 text-sm text-n-slate-10"
    >
      <span class="i-lucide-inbox size-10 mb-3 opacity-60"></span>
      暂无待推送名片
    </div>

    <!-- 列表 -->
    <div v-else class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead class="text-n-slate-10">
          <tr class="border-b border-n-weak">
            <th class="py-2 px-2 text-left font-normal">序号</th>
            <th class="py-2 px-2 text-left font-normal">姓名</th>
            <th class="py-2 px-2 text-left font-normal">电话</th>
            <th class="py-2 px-2 text-left font-normal">来源</th>
            <th class="py-2 px-2 text-left font-normal">保存时间</th>
            <th class="py-2 px-2 text-left font-normal">归属</th>
            <th class="py-2 px-2 text-left font-normal">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(it, i) in displayList"
            :key="it.id"
            class="border-b border-n-weak/60 text-n-slate-12"
          >
            <td class="py-2 px-2 text-n-slate-10">
              {{ (page - 1) * perPage + i + 1 }}
            </td>
            <td class="py-2 px-2">{{ it.name || '-' }}</td>
            <td class="py-2 px-2">{{ stripPhone(it.phoneNumber) }}</td>
            <td class="py-2 px-2 text-n-slate-11">
              {{ it.customAttributes?.source || '-' }}
            </td>
            <td class="py-2 px-2 text-n-slate-11 whitespace-nowrap">
              {{ formatSavedTime(it.customAttributes?.contactSavedAt) }}
            </td>
            <td class="py-2 px-2 text-n-slate-11">
              {{ it.customAttributes?.owner || '-' }}
            </td>
            <td class="py-2 px-2">
              <span
                v-if="pushState[it.id]?.status === 'pushing'"
                class="text-xs text-n-slate-10"
                >推送中...</span
              >
              <span
                v-else-if="pushState[it.id]?.status === 'ok'"
                class="text-xs text-green-600"
                >已推送</span
              >
              <span
                v-else-if="pushState[it.id]?.status === 'fail'"
                class="text-xs text-red-500"
                :title="pushState[it.id]?.msg"
                >失败</span
              >
              <button
                v-else
                type="button"
                class="h-7 px-3 rounded-md text-xs text-white bg-n-brand hover:opacity-90 disabled:opacity-50"
                :disabled="!pushEnabled || batchPushing"
                @click="pushOne(it)"
              >
                推送
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 分页 -->
    <div v-if="contacts.length" class="flex items-center justify-end gap-2 pt-2">
      <button
        type="button"
        class="h-8 px-3 rounded-md text-sm border border-n-weak text-n-slate-11 disabled:opacity-50"
        :disabled="page <= 1 || isFetching"
        @click="prev"
      >
        上一页
      </button>
      <span class="text-sm text-n-slate-11">第 {{ page }} 页</span>
      <button
        type="button"
        class="h-8 px-3 rounded-md text-sm border border-n-weak text-n-slate-11 disabled:opacity-50"
        :disabled="!hasMore || isFetching"
        @click="next"
      >
        下一页
      </button>
    </div>
  </div>
</template>
